import * as React from 'react';
import { useMutation } from '@tanstack/react-query';
import { RadioTower } from 'lucide-react';
import {
  PortalStatusDetail,
  todayIso,
  type PortalStatusDetail as PortalStatusDetailCode,
} from '@clawhire/shared';
import { api } from '@/lib/api';
import { enumOptions } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { DateInput } from '@/components/ui/date-input';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SimpleSelect } from '@/components/ui/select';
import { toast } from '@/components/ui/toaster';
import { useInvalidatePositions } from '../queries';
import type { PortalCheckResponse, PositionDetail } from '../types';

/** "Record portal check": status detail + date + note → `POST /positions/:id/portal-check`. */
export function PortalCheckForm({ position }: { position: PositionDetail }) {
  const invalidate = useInvalidatePositions();
  const [open, setOpen] = React.useState(false);
  const [status, setStatus] = React.useState<PortalStatusDetailCode>(position.portalStatusDetail);
  const [checkedAt, setCheckedAt] = React.useState<string | null>(todayIso());
  const [note, setNote] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setStatus(position.portalStatusDetail);
      setCheckedAt(todayIso());
      setNote('');
    }
  }, [open, position.portalStatusDetail]);

  const record = useMutation({
    mutationFn: () =>
      api.post<PortalCheckResponse>(`/positions/${encodeURIComponent(position.id)}/portal-check`, {
        status,
        checkedAt: checkedAt ?? undefined,
        note: note.trim() || undefined,
      }),
    onSuccess: async (res) => {
      toast.success(
        res.changed
          ? `Portal status → ${PortalStatusDetail.label(res.current)}`
          : `Portal check recorded (still ${PortalStatusDetail.label(res.current)})`,
      );
      setOpen(false);
      await invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm">
          <RadioTower aria-hidden="true" /> Record portal check
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80">
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            record.mutate();
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="portal-check-status">Portal status</Label>
            <SimpleSelect
              id="portal-check-status"
              value={status}
              options={enumOptions(PortalStatusDetail)}
              onChange={(v) => v && setStatus(v as PortalStatusDetailCode)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="portal-check-date">Checked on</Label>
            <DateInput id="portal-check-date" value={checkedAt} onChange={setCheckedAt} className="w-full" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="portal-check-note">Note (optional)</Label>
            <Input
              id="portal-check-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. 3 CVs already submitted"
              maxLength={500}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" size="sm" loading={record.isPending}>
              Record
            </Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
}
